import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp,
  FaExclamationTriangle,
  FaExpand,
  FaImages,
  FaPhotoVideo,
  FaRegImages,
  FaTimes,
} from "react-icons/fa";

import Reveal from "../../../components/Reveal/Reveal";
import { getAllGalleries } from "../../../services/gallerieServices";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";
const VISIBLE_COUNT = 3;

const isExternalURL = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const getImageUrl = (url) => {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (!cleanUrl) return "";

  if (
    isExternalURL(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  const cleanBaseUrl = BASE_URL.replace(/\/$/, "");

  if (cleanUrl.startsWith("/")) {
    return `${cleanBaseUrl}${cleanUrl}`;
  }

  return `${cleanBaseUrl}/${cleanUrl}`;
};

const formatDate = (value) => {
  if (!value) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const stripHtml = (html = "") => String(html || "").replace(/<[^>]*>/g, "");

const truncateText = (text = "", max = 150) => {
  const clean = String(text).trim().replace(/\s+/g, " ");

  if (clean.length <= max) return clean;

  const slice = clean.slice(0, max);
  const cut = slice.slice(0, slice.lastIndexOf(" ")) || slice;

  return `${cut}...`;
};

const isActive = (value) => {
  if (value === undefined || value === null || value === "") return true;

  return value === true || value === 1 || value === "1";
};

const getGalleryTitle = (gallery) =>
  gallery?.titre ||
  gallery?.title ||
  gallery?.nom_galerie ||
  gallery?.name ||
  "Album photo";

const getGalleryDate = (gallery) =>
  gallery?.date_publication ||
  gallery?.published_at ||
  gallery?.createdAt ||
  gallery?.created_at ||
  gallery?.updatedAt ||
  gallery?.updated_at;

const getGalleryPhotos = (gallery) => {
  const photos = Array.isArray(gallery?.photos) ? gallery.photos : [];

  return photos.filter((photo) => isActive(photo?.is_active));
};

const useLockBody = (locked) => {
  useEffect(() => {
    if (!locked) return undefined;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [locked]);
};

const SkeletonCard = () => (
  <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
    <div className="h-72 animate-pulse bg-slate-200 dark:bg-slate-800" />
    <div className="space-y-4 p-5">
      <div className="h-5 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      <div className="h-4 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      <div className="h-4 w-4/5 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      <div className="h-10 w-36 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
    </div>
  </div>
);

const EmptyState = () => (
  <div className="col-span-full rounded-[2rem] border border-dashed border-slate-300 bg-white/75 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
      <FaRegImages />
    </div>

    <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
      Aucun album disponible
    </h3>

    <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
      Les albums photos du club apparaîtront ici dès qu’ils seront publiés dans
      l’espace d’administration.
    </p>
  </div>
);

const ErrorBanner = ({ message }) => (
  <div className="mb-8 rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
    <div className="flex items-center justify-center gap-3">
      <FaExclamationTriangle />
      {message}
    </div>
  </div>
);

const GalleryCard = ({ gallery, index, onOpen }) => {
  const title = getGalleryTitle(gallery);
  const photos = getGalleryPhotos(gallery);
  const coverPhoto = photos[0];
  const cover = getImageUrl(coverPhoto?.photo_url) || "/img/cours_judo.jpg";
  const dateStr = formatDate(getGalleryDate(gallery));
  const shortDescription = truncateText(stripHtml(gallery?.description || ""), 135);
  const count = photos.length;

  return (
    <article
      className="group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-slate-900"
      onClick={() => onOpen(gallery)}
    >
      <div className="relative h-72 overflow-hidden bg-slate-200 dark:bg-slate-800">
        <img
          src={cover}
          alt={title}
          loading={index === 0 ? "eager" : "lazy"}
          decoding="async"
          className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/25 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-primary/30 opacity-0 transition duration-500 group-hover:opacity-100" />

        <div className="absolute left-4 top-4">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/15 px-3 py-1.5 text-xs font-bold text-white backdrop-blur-xl">
            <FaImages className="text-primary" />
            {count} photo{count > 1 ? "s" : ""}
          </span>
        </div>

        <button
          type="button"
          className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/15 text-white opacity-0 shadow-lg backdrop-blur-xl transition group-hover:opacity-100 hover:scale-105 hover:bg-white/25"
          aria-label={`Ouvrir l'album ${title}`}
        >
          <FaExpand />
        </button>

        <div className="absolute bottom-4 left-4 right-4">
          {dateStr && (
            <span className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold text-white/85 backdrop-blur-xl">
              <FaCalendarAlt className="text-primary" />
              {dateStr}
            </span>
          )}

          <h3 className="line-clamp-2 font-barlow text-2xl font-bold leading-tight text-white drop-shadow-lg">
            {title}
          </h3>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
          {shortDescription ||
            "Revivez les moments forts du club à travers cet album photo."}
        </p>

        <div className="mt-auto pt-6">
          <button
            type="button"
            className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-primary dark:bg-white dark:text-slate-950 dark:hover:bg-primary dark:hover:text-white"
            aria-label={`Voir l'album ${title}`}
          >
            <FaPhotoVideo />
            Voir l’album
          </button>
        </div>
      </div>
    </article>
  );
};

const GalleryModal = ({ gallery, onClose, onOpenLightbox }) => {
  const photos = getGalleryPhotos(gallery);
  const title = getGalleryTitle(gallery);
  const dateStr = formatDate(getGalleryDate(gallery));

  useLockBody(true);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`Album ${title}`}
      onClick={onClose}
    >
      <article
        className="relative max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-950"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 px-5 py-4 backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/90 sm:px-7">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-3 py-1.5 text-xs font-bold text-orange-800 dark:bg-orange-500/10 dark:text-orange-300">
                  <FaImages />
                  {photos.length} photo{photos.length > 1 ? "s" : ""}
                </span>

                {dateStr && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-slate-300">
                    <FaCalendarAlt className="text-primary" />
                    {dateStr}
                  </span>
                )}
              </div>

              <h3 className="truncate font-barlow text-2xl font-bold text-slate-950 dark:text-white sm:text-3xl">
                {title}
              </h3>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-800 transition hover:scale-105 dark:bg-white/10 dark:text-white"
              aria-label="Fermer l'album"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="max-h-[calc(92vh-98px)] overflow-y-auto px-4 pb-6 pt-5 sm:px-6">
          {gallery?.description && (
            <p className="mb-5 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 text-sm leading-7 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
              {stripHtml(gallery.description)}
            </p>
          )}

          {photos.length === 0 ? (
            <div className="rounded-[2rem] border border-dashed border-slate-300 p-10 text-center text-slate-500 dark:border-white/10 dark:text-slate-400">
              Aucune photo dans cet album.
            </div>
          ) : (
            <div className="columns-1 gap-4 sm:columns-2 lg:columns-3">
              {photos.map((photo, index) => {
                const src = getImageUrl(photo.photo_url);

                return (
                  <button
                    key={photo.id || `${photo.photo_url}-${index}`}
                    type="button"
                    onClick={() => onOpenLightbox(index)}
                    className="group relative mb-4 w-full break-inside-avoid overflow-hidden rounded-[1.5rem] bg-slate-100 shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-1 hover:shadow-xl dark:bg-slate-900 dark:ring-white/10"
                    aria-label={`Agrandir la photo ${index + 1}`}
                  >
                    <img
                      src={src}
                      alt={photo?.titre || photo?.description || "Photo"}
                      loading="lazy"
                      decoding="async"
                      className="h-auto w-full object-cover transition duration-700 group-hover:scale-105"
                    />

                    <div className="absolute inset-0 flex items-center justify-center bg-slate-950/0 opacity-0 transition group-hover:bg-slate-950/30 group-hover:opacity-100">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg">
                        <FaExpand />
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </article>
    </div>
  );
};

const Lightbox = ({ photos, index, setIndex, onClose }) => {
  const total = photos.length;
  const current = photos[index];
  const src = getImageUrl(current?.photo_url);

  useLockBody(true);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }

      if (event.key === "ArrowLeft") {
        setIndex((prev) => (prev - 1 + total) % total);
      }

      if (event.key === "ArrowRight") {
        setIndex((prev) => (prev + 1) % total);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, setIndex, total]);

  useEffect(() => {
    if (total < 2) return;

    const next = new Image();
    next.src = getImageUrl(photos[(index + 1) % total]?.photo_url);

    const previous = new Image();
    previous.src = getImageUrl(photos[(index - 1 + total) % total]?.photo_url);
  }, [index, photos, total]);

  if (!current || !src) return null;

  const goPrevious = () => {
    setIndex((prev) => (prev - 1 + total) % total);
  };

  const goNext = () => {
    setIndex((prev) => (prev + 1) % total);
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/95 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Visionneuse photo"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 z-30 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition hover:scale-105"
        aria-label="Fermer la photo"
      >
        <FaTimes />
      </button>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              goPrevious();
            }}
            className="absolute left-4 top-1/2 z-30 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-white/15 text-white shadow-xl backdrop-blur-xl transition hover:scale-105 hover:bg-white/25"
            aria-label="Photo précédente"
          >
            <FaArrowLeft />
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              goNext();
            }}
            className="absolute right-4 top-1/2 z-30 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-white/15 text-white shadow-xl backdrop-blur-xl transition hover:scale-105 hover:bg-white/25"
            aria-label="Photo suivante"
          >
            <FaArrowRight />
          </button>
        </>
      )}

      <div
        className="relative max-h-[90vh] max-w-[94vw]"
        onClick={(event) => event.stopPropagation()}
      >
        <img
          src={src}
          alt={current?.titre || current?.description || "Photo"}
          className="max-h-[82vh] max-w-[94vw] rounded-[1.5rem] object-contain shadow-2xl"
          loading="eager"
        />

        <div className="mt-4 flex flex-col gap-2 rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-white backdrop-blur-xl sm:flex-row sm:items-center sm:justify-between">
          <p className="truncate text-sm font-semibold">
            {current?.titre || current?.description || "Photo"}
          </p>

          <p className="shrink-0 text-sm font-bold text-white/70">
            {index + 1} / {total}
          </p>
        </div>
      </div>
    </div>
  );
};

export default function LastGallerie() {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchGalleries = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const response = await getAllGalleries();

        if (!mounted) return;

        const activeGalleries = (Array.isArray(response) ? response : [])
          .filter((gallery) => isActive(gallery?.is_active))
          .sort((a, b) => {
            const dateA = new Date(getGalleryDate(a) || 0).getTime();
            const dateB = new Date(getGalleryDate(b) || 0).getTime();

            return dateB - dateA;
          });

        setGalleries(activeGalleries);
      } catch (error) {
        console.error("Erreur lors du chargement des albums :", error);

        if (mounted) {
          setErrorMsg("Impossible de charger les albums photos.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchGalleries();

    return () => {
      mounted = false;
    };
  }, []);

  const visibleGalleries = useMemo(() => {
    return showAll ? galleries : galleries.slice(0, VISIBLE_COUNT);
  }, [galleries, showAll]);

  const selectedPhotos = useMemo(() => {
    return getGalleryPhotos(selectedGallery);
  }, [selectedGallery]);

  const hasMore = galleries.length > VISIBLE_COUNT;

  const openGallery = useCallback((gallery) => {
    setSelectedGallery(gallery);
    setLightboxIndex(null);
  }, []);

  const closeGallery = useCallback(() => {
    setSelectedGallery(null);
    setLightboxIndex(null);
  }, []);

  const openLightboxAt = useCallback((index) => {
    setLightboxIndex(index);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
  }, []);

  return (
    <section className="relative overflow-hidden bg-slate-50 py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.13)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.13)_1px,transparent_1px)] bg-[size:42px_42px] opacity-35 dark:opacity-15" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaRegImages />
            Albums photos
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Dernières{" "}
            <span className="text-primary">
              galeries
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Revivez les moments forts du club : événements, entraînements,
            stages, compétitions et instants partagés.
          </p>
        </Reveal>

        {errorMsg && <ErrorBanner message={errorMsg} />}

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {loading &&
            Array.from({ length: VISIBLE_COUNT }).map((_, index) => (
              <SkeletonCard key={`gallery-skeleton-${index}`} />
            ))}

          {!loading && visibleGalleries.length === 0 && <EmptyState />}

          {!loading &&
            visibleGalleries.map((gallery, index) => (
              <Reveal
                key={gallery.id || `${getGalleryTitle(gallery)}-${index}`}
                variant="zoom"
                delay={index * 90}
              >
                <GalleryCard
                  gallery={gallery}
                  index={index}
                  onOpen={openGallery}
                />
              </Reveal>
            ))}
        </div>

        {!loading && hasMore && (
          <Reveal delay={120} className="mt-10 text-center">
            <button
              type="button"
              onClick={() => setShowAll((value) => !value)}
              className="inline-flex items-center justify-center gap-3 rounded-full bg-slate-950 px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-primary dark:bg-white dark:text-slate-950 dark:hover:bg-primary dark:hover:text-white"
            >
              {showAll ? (
                <>
                  Voir moins
                  <FaChevronUp />
                </>
              ) : (
                <>
                  Voir tous les albums
                  <FaChevronDown />
                </>
              )}
            </button>
          </Reveal>
        )}
      </div>

      {selectedGallery && (
        <GalleryModal
          gallery={selectedGallery}
          onClose={closeGallery}
          onOpenLightbox={openLightboxAt}
        />
      )}

      {selectedGallery && lightboxIndex !== null && selectedPhotos.length > 0 && (
        <Lightbox
          photos={selectedPhotos}
          index={lightboxIndex}
          setIndex={setLightboxIndex}
          onClose={closeLightbox}
        />
      )}
    </section>
  );
}